/* HCORZ
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 11/07/2025 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import { createContext, useContext, useState } from "react";
import { createTheme } from "@mui/material";
import { esES } from "@mui/material/locale";
import { useLayout } from "./LayoutContext"

const getPaginadoModel = () => ({
  totalDeRegistros: { value: 0, error: false },
  totalDePaginas: { value: 0, error: false },
  registroPorPagina: { value: 10, error: false },
  pagina: { value: 1, error: false },
  reiniciarPagina: { value: false, error: false },
  sByOrder: { value: "Asc", error: false },
  sField: { value: "", error: false },
});

const getConfiguracionDefault = () => ({
  tituloNavegador: "SISESOFTX",
  tituloDependencia: "PLATAFORMA DE SISESOFTX",
  metaDescription: "Plataforma de SiseSoftx",
  colorPrimario: "#d97706",//#FD8204
  colorSecundario: "#111827",//#16216A
  contrastePrimario: "#FFFFFF",
  contrasteSecundario: "#FFFFFF",
  urlFuente: "https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap",
  nombreFuente: "Roboto",
  rutaImagenFondo: "",
  rutaImagenLogo: "src/img/logoHcorz.svg",
  rutaImagenPortal: "src/img/logoHcorz.jpg",
});

export const ConfiguracionContext = createContext();
export const ConfiguracionProvider = (props) => {
  const [titlePage, setTitlePage] = useState("Hcorz");

  const [paginadoModel, setPaginadoModel] = useState(getPaginadoModel());


  //Uso de componentes padre solamente ThemeProvider
  //#a9701e Color básico 1  marrón anaranjado para ventanas flex.
  //#191970 Color básico barra de menus lateral



  const [theme, setTheme] = useState(
    createTheme({
      palette: {
        primary: {
          main: "#d97706", // Amber 600
          contrastText: "#FFFFFF",
        },
        secondary: {
          main: "#111827", // Gray 900
          contrastText: "#FFFFFF",
        },
        green: {
          main: "#4caf50",
          contrastText: "#FFFFFF",
        },
        blue: {
          main: "#4fc3f7",
          contrastText: "#FFFFFF",
        },
        red: {
          main: "#ef5350",
          contrastText: "#FFFFFF",
        },
        orange: {
          main: "#ff9800",
          contrastText: "#FFFFFF",
        },
      },
    })
  );

  const handleChangeConfiguration = (
    urlFont,
    font,
    primaryColor,
    secundaryColor,
    primaryContrast,
    secondaryContrast,
    titlePage,
    metaDescription
  ) => {
    document.getElementById("Hcorz-title-page").innerHTML = titlePage;
    document.getElementById("Hcorz-meta").setAttribute("content", metaDescription);
    document.getElementById("Hcorz-urlfont").setAttribute("href", urlFont);
    setTitlePage(titlePage);

    setTheme(
      createTheme(
        {
          typography: {
            fontFamily: font,
          },
          palette: {
            primary: {
              main: primaryColor,
              contrastText: primaryContrast,
            },
            secondary: {
              main: secundaryColor,
              contrastText: secondaryContrast,
            },
            green: {
              main: "#4caf50",
              contrastText: "#FFFFFF",
            },
            blue: {
              main: "#4fc3f7",
              contrastText: "#FFFFFF",
            },
            red: {
              main: "#ef5350",
              contrastText: "#FFFFFF",
            },
            orange: {
              main: "#ff9800",
              contrastText: "#FFFFFF",
            },
          },
          components: {
            MuiButton: {
              defaultProps: {
                variant: "contained",
                color: "primary",
              },
              styleOverrides: {
                root: {
                  boxShadow: "none",
                  borderRadius: 10,
                  textTransform: "initial",
                },
              },
            },
            MuiIconButton: {
              defaultProps: {
                color: "warning",
              },
              styleOverrides: {
                root: {
                  backgroundColor: "white",
                  ":hover": {
                    backgroundColor: "white",
                    '& svg': {
                      fill: secundaryColor,
                    },
                  },
                  ":disabled": {
                    backgroundColor: "white",
                  },
                },
              },
            },
            MuiTextField: {
              defaultProps: {
                variant: "outlined",
                color: "secondary",
                fullWidth: true,
                size: "small",
              },
            },
            MuiDialog: {
              defaultProps: {
                fullWidth: true,
                maxWidth: "sm",
              },
            },
            MuiDrawer: {
              defaultProps: {
                BackdropProps: {
                  style: {
                    backgroundColor: "rgba(0, 0, 0, 0.4)",
                  },
                },
              },
            },
            MuiGrid: {
              defaultProps: {
                spacing: 2,
              },
            },
            MuiTable: {
              defaultProps: {
                size: "small",
              },
            },
            MuiTooltip: {
              styleOverrides: {
                tooltip: {
                  fontSize: 14,
                },
              },
              defaultProps: {
                arrow: false,
                placement: "bottom",
              },
            },
            MuiPaper: {
              defaultProps: {
                elevation: 3,
                variant: "elevation",
                //variant: "outlined",
              },
            },
            MuiFormLabel: {
              styleOverrides: {
                asterisk: {
                  color: "#ff8400",
                  fontWeight: "700",
                  fontSize: "18px",
                },
                root: {
                  color: "rgba(0, 0, 0, 0.9)",
                },
              },
            },
            MuiTab: {
              styleOverrides: {
                root: {
                  "&.Mui-selected": {
                    backgroundColor: primaryColor,
                    color: primaryContrast,
                  },
                },
              },
            },
          },
        },
        esES
      )
    );
  };

  const handleChangePaginacionModel = async (e, value) => {
    setPaginadoModel({
      ...paginadoModel,
      [e]: {
        value: value,
        error: false,
      },
    });
  };


  return (
    <ConfiguracionContext.Provider
      value={{
        theme,
        titlePage,
        paginadoModel,
        handleChangeConfiguration,
        setPaginadoModel,
        getPaginadoModel,
        handleChangePaginacionModel,
      }}>
      {props.children}
    </ConfiguracionContext.Provider>
  );
};

export const useConfiguracion = () => useContext(ConfiguracionContext);
